import * as d3 from 'd3';
import cx from 'classnames';
import i18next from 'i18next';

import { getUnit } from '../../Pdfs/TransferGraph/helpers';
import { applyThousandsSeparator } from '../../../utils/strings';

import { renderTickFormatXAxis } from './helpers';
import styles from './TransferGraphD3.module.scss';
import { ParameterType, TransferMetricEntry } from './interfaces';

let capsuleActive: string;

const TIME_TRANSITION = 300;
const TICK_PADDING = 6;
const START_POSITION_Y = 0;

const DEFAULT_RADIO_CIRCLE = 7;
const ACTIVE_RADIO_CIRCLE = 9;

interface Props {
  container: HTMLDivElement | null;
  transferMetrics: TransferMetricEntry[];
  parameter: ParameterType;
  width: number;
  height: number;
  circleRadioDefault?: number;
  circleRadioActive?: number;
}

export class TransferGraphD3 {
  container: HTMLDivElement | null;
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined> = d3.select<SVGSVGElement, unknown>(document.createElementNS('http://www.w3.org/2000/svg', 'svg'));
  groupMain: d3.Selection<SVGGElement, unknown, null, undefined>;

  xScaleBand: d3.ScaleBand<string> = d3.scaleBand();
  yScaleLinear: d3.ScaleLinear<number, number, never> | d3.ScaleSymLog<number, number, never> = d3.scaleLinear();

  axisBottom: d3.Selection<SVGGElement, unknown, null, undefined> = d3.select<SVGGElement, unknown>(document.createElementNS('http://www.w3.org/2000/svg', 'g'));
  axisLeft: d3.Selection<SVGGElement, unknown, null, undefined> = d3.select<SVGGElement, unknown>(document.createElementNS('http://www.w3.org/2000/svg', 'g'));

  tooltip: d3.Selection<HTMLDivElement, unknown, null, undefined> = d3.select<HTMLDivElement, unknown>(document.createElement('div'));

  transferMetrics: TransferMetricEntry[] = [];
  parameter: ParameterType;
  width: number;
  height: number;
  margin = { top: 20, right: 30, bottom: 22, left: 74 };

  circleRadioDefault: number;
  circleRadioActive: number;

  // eslint-disable-next-line
  constructor(props: Props) {
    const { container, transferMetrics, parameter, width, height, circleRadioDefault = DEFAULT_RADIO_CIRCLE, circleRadioActive = ACTIVE_RADIO_CIRCLE } = props;

    this.container = container;
    this.transferMetrics = transferMetrics;
    this.parameter = parameter;

    this.circleRadioDefault = circleRadioDefault;
    this.circleRadioActive = circleRadioActive;

    this.width = width - this.margin.left - this.margin.right;
    this.height = height - this.margin.top - this.margin.bottom;

    d3.select(container).select('svg').remove();

    this.svg = d3.select(container)
      .append('svg')
      .attr('id', 'svg')
      .attr('width', this.width + this.margin.left + this.margin.right)
      .attr('height', this.height + this.margin.top + this.margin.bottom);

    this.groupMain = this.svg
      .append('g')
      .attr('id', 'content')
      .attr('transform', `translate( ${this.margin.left}, ${this.margin.top} )`);

    this.createTooltip();
    this.updateDataPoints();
    this.renderTooltips();
    this.renderLineSelected();
  }

  updateDataPoints = () => {
    this.renderLinesAxis();

    this.buildAxisX();
    this.buildAxisY();

    this.deleteDomainAxisX();

    this.drawXAxis();
    this.drawYAxis();

    this.renderLines();
    this.renderPoints();
  };

  generateLines = () => {
    const line = d3.line<TransferMetricEntry>()
      .x(d => (this.xScaleBand(d.name) || 0) + (this.xScaleBand.bandwidth() / 2))
      .y(d => this.yScaleLinear(d.value))
      .curve(d3.curveCatmullRom);

    return { line };
  };

  renderLines = () => {
    const { transferMetrics, groupMain } = this;

    const { line } = this.generateLines();

    groupMain
      .append('path')
      .datum(transferMetrics)
      .attr('class', styles.transferLines)
      .attr('d', line);
  };

  renderPoints () {
    const { groupMain, transferMetrics, circleRadioDefault } = this;

    groupMain.append('g')
      .selectAll('circle')
      .data(transferMetrics)
      .enter()
      .append('circle')
      .attr('id', (value, index) => {
        return `capsule-${index}`;
      })
      .attr('class', cx('circle', styles.circle))
      .attr('r', circleRadioDefault)
      .attr('cx', (capsule) => (this.xScaleBand(capsule.name) || 0) + (this.xScaleBand.bandwidth() / 2))
      .attr('cy', (point) => this.yScaleLinear(point.value));
  }

  deleteDomainAxisX () {
    const { container } = this;
    d3.select(container).select('#content').select('#axisX').selectAll('.domain').remove();
  }

  renderLinesAxis = () => {
    const { container, groupMain, height, width } = this;

    d3.select(container).selectAll('.lineAxisY').remove();
    d3.select(container).selectAll('.lineAxisX').remove();

    groupMain.append('line')
      .attr('class', cx(styles.lines, 'lineAxisY'))
      .attr('y1', START_POSITION_Y)
      .attr('y2', height)
      .attr('x1', 0)
      .attr('x2', 0);

    groupMain.append('line')
      .attr('class', cx(styles.lines, 'lineAxisY'))
      .attr('y1', START_POSITION_Y)
      .attr('y2', height)
      .attr('x1', width)
      .attr('x2', width);

    groupMain.append('line')
      .attr('class', cx(styles.lines, 'lineAxisX'))
      .attr('y1', height)
      .attr('y2', height)
      .attr('x1', 0)
      .attr('x2', width);

    groupMain.append('line')
      .attr('class', cx(styles.lines, 'lineAxisX'))
      .attr('y1', START_POSITION_Y)
      .attr('y2', START_POSITION_Y)
      .attr('x1', 0)
      .attr('x2', width);
  }

  updateAxis () {
    const { height } = this;

    this.renderLinesAxis();

    this.buildAxisX();
    this.buildAxisY();

    const axisBottom = d3.axisBottom(this.xScaleBand)
      .tickFormat((d) => renderTickFormatXAxis({ domainValue: d }))
      .tickSize(-height)
      .tickPadding(TICK_PADDING);

    this.axisBottom
      .attr('fill', 'transparent')
      .attr('transform', `translate(0, ${this.height})`)
      .transition()
      .duration(TIME_TRANSITION)
      .call(axisBottom);

    this.deleteDomainAxisX();

    const axiLeft = d3.axisLeft(this.yScaleLinear)
      .tickFormat((d) => d3.format('')(d as number))
      .tickSize(0)
      .tickPadding(TICK_PADDING);

    this.axisLeft.transition()
      .duration(TIME_TRANSITION)
      .call(axiLeft);
  }
  
  refreshChart (props: { transferMetrics: TransferMetricEntry[]; width: number; height: number; parameter: ParameterType; }) {
    const { transferMetrics, width, height, parameter } = props;
    const { container } = this;
    
    this.transferMetrics = transferMetrics;
    this.parameter = parameter;
    
    d3.select(container).select('#tooltipContent').remove();
    d3.select(container).selectAll('.circle').remove();
    
    this.updateSize(width, height);
    this.updateDataPoints();
    this.renderTooltips();
    this.renderLineSelected();
  }

  buildAxisX () {
    const { width, transferMetrics } = this;

    this.xScaleBand = d3.scaleBand()
      .domain(transferMetrics.map(capsule => capsule.name))
      .range([0, width])
      .padding(0.1);
  }

  buildAxisY () {
    const { height } = this;
    const { maxY, minY } = this.getYDomainData();

    this.yScaleLinear = d3.scaleLinear()
      .domain([minY, maxY])
      .range([height, 0]);
  }

  getYDomainData = () => {
    const { transferMetrics } = this;

    const margin = 0.02;
    const dataY: number[] = transferMetrics.map((item) => item.value);

    const minY = Math.min(...dataY);
    const maxY = Math.max(...dataY);

    if (minY === 0) {
      return {
        minY,
        maxY: maxY + (maxY * margin),
      };
    }

    return {
      minY: minY - (minY * margin),
      maxY: maxY + (maxY * margin),
    };
  }

  drawXAxis () {
    const { height, groupMain } = this;

    const axisBottom = d3.axisBottom(this.xScaleBand)
      .tickFormat((d) => renderTickFormatXAxis({ domainValue: d }))
      .tickSize(-height)
      .tickPadding(TICK_PADDING);

    this.axisBottom = groupMain.append('g')
      .attr('id', 'axisX')
      .attr('class', cx(styles.axisX, styles.axisLight))
      .attr('transform', `translate(0, ${this.height})`)
      .call(axisBottom);

    const estimatedCharWidth = 8;
    let maxLabelLength = 0;

    this.xScaleBand.domain().forEach((label) => {
      if (label.length > maxLabelLength) {
        maxLabelLength = label.length;
      }
    });
    
    const estimatedMaxLabelWidth = estimatedCharWidth * maxLabelLength;
    const spacePerTick = this.width / this.xScaleBand.domain().length;

    if (estimatedMaxLabelWidth > spacePerTick) {
      this.axisBottom.selectAll('text')
        .attr('transform', 'rotate(90)')
        .style('text-anchor', 'end');
    }
  
    this.deleteDomainAxisX();
  }

  drawYAxis () {
    const { groupMain } = this;

    const axis = d3.axisLeft(this.yScaleLinear)
      .tickFormat((d) => applyThousandsSeparator(d.toString()))
      .ticks(5)
      .tickSize(0)
      .tickPadding(TICK_PADDING);

    this.axisLeft = groupMain.append('g')
      .attr('class', cx(styles.axisY, styles.axisLight))
      .call(axis);
  }

  resize = (props: { width: number, height: number }) => {
    const { width, height } = props;
    const { container } = this;

    d3.select(container).select('#content').selectAll('*').remove();

    this.updateSize(width, height);
    this.updateAxis();
    this.updateDataPoints();
  };

  updateSize = (width: number, height: number) => {
    const { container } = this;

    this.width = width - this.margin.left - this.margin.right;
    this.height = height - this.margin.top - this.margin.bottom;

    const _width = this.width + this.margin.left + this.margin.right;
    const _height = this.height + this.margin.top + this.margin.bottom;

    d3.select(container).select('svg')
      .attr('width', _width)
      .attr('height', _height);
  };

  createTooltip = () => {
    this.tooltip = d3.select(this.container)
      .append('div')
      .attr('id', 'tooltip')
      .attr('class', styles.tooltip)
      .style('display', 'none')
      .on('mouseover', () => {
        this.tooltip.style('display', 'block');
        d3.select('#selectedTick').style('display', 'block');
        d3.select(capsuleActive).attr('r', this.circleRadioActive);
      });
  };

  renderLineSelected = () => {
    const { container, groupMain, height } = this;
    d3.select(container).select('#selectedTick').remove();

    groupMain.append('line')
      .attr('id', 'selectedTick')
      .attr('stroke', '#909090')
      .attr('stroke-width', 1)
      .attr('y1', 0)
      .attr('y2', height)
      .style('display', 'none');
  };

  renderTooltips () {
    const { groupMain, xScaleBand, yScaleLinear, width, height, tooltip, transferMetrics, parameter, circleRadioDefault, circleRadioActive } = this;

    const tooltipContent = tooltip.append('div')
      .attr('id', 'tooltipContent')
      .attr('class', styles.content);

    let previousCapsuleName = '';

    groupMain.append('rect')
      .attr('class', 'rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', width)
      .attr('height', height)
      .style('fill', 'none')
      .style('pointer-events', 'all')
      .on('mouseout', function () {
        tooltip.style('display', 'none');
        d3.select('#selectedTick').style('display', 'none');
        d3.selectAll('.circle').attr('r', circleRadioDefault);
      })
      .on('mousemove', function (event) {
        const [xPos] = d3.pointer(event);
        const domain = xScaleBand.domain();
        const bandwidth = xScaleBand.bandwidth();

        const transportName = domain.find((d) => {
          const xStart = xScaleBand(d);
          return xStart !== undefined && xStart <= xPos && xPos < xStart + bandwidth;
        });

        if (!transportName) {
          return;
        }

        const index = transferMetrics.findIndex((transferMetric) => transferMetric.name === transportName);
        
        if (index === -1) {
          return;
        }
        const selectedObject = transferMetrics[index];

        tooltip.style('display', 'block');
        d3.select('#selectedTick').style('display', 'block');

        d3.selectAll('.circle').attr('r', circleRadioDefault);
        d3.select(capsuleActive).attr('r', circleRadioActive);

        if (previousCapsuleName === selectedObject.name) {
          return;
        }

        previousCapsuleName = selectedObject.name;
        const value = selectedObject.value;

        const container = document.getElementById('transfer_metric_chart');
        const width = container?.clientWidth || 0;
        
        const marginLeft = (xScaleBand(previousCapsuleName) || 0) + (xScaleBand.bandwidth() / 2) ;
        const marginBottom = yScaleLinear(value);

        capsuleActive = `#capsule-${index}`;

        const tooltipDialogWidth = 140;
        const bubbleWidth = 16;
        const tooltipTotalWidth = tooltipDialogWidth + bubbleWidth;

        if (marginLeft + tooltipTotalWidth < width) {
          tooltip.classed(styles.rightAlignedTooltip, false);
          tooltip.classed(styles.leftAlignedTooltip, true);
        } else {
          tooltip.classed(styles.rightAlignedTooltip, true);
          tooltip.classed(styles.leftAlignedTooltip, false);
        }

        tooltip
          .style('left', () => {
            if (marginLeft + tooltipTotalWidth < width) {
              const tooltipXCoordinate = marginLeft + (tooltipDialogWidth / 2) + (bubbleWidth / 2);
              return `${tooltipXCoordinate}px`;
            }
            const tooltipXCoordinate = marginLeft - (tooltipDialogWidth / 2) - (bubbleWidth * 2);
            return `${tooltipXCoordinate}px`;
          })
          .style('bottom', () => {
            const tooltipTotalHeight = tooltip.node()?.offsetHeight || 0;
            const value = height - marginBottom + (tooltipTotalHeight / 2.5);
            return `${value}px`;
          });

        d3.select('#selectedTick')
          .attr('x1', marginLeft)
          .attr('x2', marginLeft);

        tooltipContent.selectAll('*').remove();

        const entry = tooltipContent
          .append('div')
          .attr('class', styles.entry);

        const entryContent = entry.append('div')
          .attr('class', styles.entryContent);

        entryContent.append('div')
          .attr('class', styles.stat)
          .html(`${i18next.t('transfers.value')}: <strong>${applyThousandsSeparator(value)} ${getUnit({ parameter })}</strong>`);
      });
  }
}
